import {
  SubmitWttpQuestionnaireResponseDocument,
  UpdateWttpQuestionnaireQuestionResponseDto,
  UpdateWttpQuestionnaireQuestionResponsesDocument,
  WttpQuestion,
  WttpQuestionResponse,
  WttpQuestionnaireResponse,
  WttpQuestionnaireResponseDocument,
  WttpQuestionnaireResponseStatus,
  WttpQuestionnaireResponsesDocument,
} from 'src/graphql/generated/graphql';
import WttpQuestionnaireResponseQuestionItem from './question-item';
import { Alert, BottomNavigation, Button, Card } from '@otp/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { QuestionResponseForm } from '../question-responses/form';

type WttpQuestionnaireResponseQuestionsListProps = {
  questionnaireResponse: Pick<
    WttpQuestionnaireResponse,
    'id' | 'status' | 'storeId' | 'expiresAt'
  >;
  questions: Pick<WttpQuestion, 'id' | 'title' | 'category' | 'fileAssets'>[];
  responses: Partial<WttpQuestionResponse>[];
  onClose: () => void;
};

export const WttpQuestionnaireResponseQuestionsList = ({
  questionnaireResponse,
  questions,
  responses,
  onClose,
}: WttpQuestionnaireResponseQuestionsListProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'wttp.questionnaire-response',
  });

  const [updateWttpQuestionnaireResponse] = useMutation(
    UpdateWttpQuestionnaireQuestionResponsesDocument,
    {
      refetchQueries: [
        {
          query: WttpQuestionnaireResponseDocument,
          variables: {
            id: questionnaireResponse.id,
          },
        },
      ],
    },
  );

  const [submitWttpQuestionnaireResponse] = useMutation(
    SubmitWttpQuestionnaireResponseDocument,
    {
      refetchQueries: [
        {
          query: WttpQuestionnaireResponseDocument,
          variables: {
            id: questionnaireResponse.id,
          },
        },
      ],
    },
  );

  const [displayAlert, setDisplayAlert] = useState(false);

  const [detailsOpenForQuestionNumber, setDetailsOpenForQuestionNumber] =
    useState<number | undefined>();

  const detailsOpenForQuestion =
    detailsOpenForQuestionNumber !== undefined
      ? questions[detailsOpenForQuestionNumber]
      : undefined;

  const responsesMap = new Map(responses?.map((r) => [r.questionId, r]));

  const isFullyAnswered = responses.length === questions.length;
  const isCompleted =
    questionnaireResponse.status === WttpQuestionnaireResponseStatus.Completed;
  const isExpired = questionnaireResponse.expiresAt
    ? new Date() > new Date(questionnaireResponse.expiresAt) ||
      questionnaireResponse.status === WttpQuestionnaireResponseStatus.Expired
    : false;
  const isReadonly = isCompleted || isExpired;

  const onUpdateWttpQuestionResponse = (
    questionResponse: UpdateWttpQuestionnaireQuestionResponseDto,
  ) => {
    updateWttpQuestionnaireResponse({
      variables: {
        id: questionnaireResponse.id,
        data: [questionResponse],
      },
    });
  };

  const onSubmit = async () => {
    if (isFullyAnswered) {
      await submitWttpQuestionnaireResponse({
        variables: {
          id: questionnaireResponse.id,
        },
      });

      onClose();
    } else {
      setDisplayAlert(true);
    }
  };

  return (
    <>
      <div className="w-full sm:min-w-medium">
        <div className="flex flex-col gap-2 ">
          {questions?.map((question, order) => (
            <WttpQuestionnaireResponseQuestionItem
              key={question.id}
              order={order}
              question={question}
              response={responsesMap.get(question.id)}
              onUpdateWttpQuestionResponse={onUpdateWttpQuestionResponse}
              onOpenQuestionDetails={() => {
                setDetailsOpenForQuestionNumber(order);
              }}
              readonly={isReadonly}
            />
          ))}
        </div>
        <BottomNavigation>
          <Button variant="contained" className="w-full" onClick={onClose}>
            {isReadonly && t('close')}
            {!isReadonly && t('save-and-close')}
          </Button>
          {!isReadonly && (
            <Button variant="contained" className="w-full" onClick={onSubmit}>
              {t('submit')} &#8594;
            </Button>
          )}
        </BottomNavigation>
      </div>
      <Alert
        message={t('alert-message')}
        open={displayAlert}
        onClose={() => {
          setDisplayAlert(false);
        }}
      />
      {detailsOpenForQuestion && (
        <QuestionResponseForm
          key={detailsOpenForQuestion.id}
          storeId={questionnaireResponse.storeId}
          question={detailsOpenForQuestion}
          response={responsesMap.get(detailsOpenForQuestion.id)}
          onUpdateWttpQuestionResponse={onUpdateWttpQuestionResponse}
          onClose={() => setDetailsOpenForQuestionNumber(undefined)}
          onNavigateToNextQuestion={() => {
            setDetailsOpenForQuestionNumber(
              (detailsOpenForQuestionNumber || 0) + 1,
            );
          }}
          readonly={isReadonly}
        />
      )}
    </>
  );
};

export default WttpQuestionnaireResponseQuestionsList;
